import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';

interface CreatePromoPayload {
    plan_id: string;
    event_id: string;
    promotion_id: string | null;
    attributes: Record<string, unknown>;
    name: string;
    start_date: string;
    end_date: string;
    cra_start_date: string;
    cra_end_date: string;
    ppg_ids: number[];
    offer: Record<string, unknown> | null;
    changes: { ppg_id: string; metric_key: string; old_value?: number | null; value: number }[];
    user_id: string;
}

const createPromo = async (payload: CreatePromoPayload) => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.createPromo, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        data: payload,
    });
    return response.data;
};

interface CreatePromoResponse {
    promotion_id?: string;
    [key: string]: unknown;
}

const useCreatePromoMutation = ({ onSuccess }: { onSuccess?: (data: CreatePromoResponse) => void } = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: CreatePromoPayload) => createPromo(payload),
        onSuccess: (data, variables) => {
            const promoId = data?.promotion_id ?? variables.promotion_id;
            if (promoId) {
                queryClient.invalidateQueries({ queryKey: tpoGenKeys.promoDetails(promoId) });
            }
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.eventDetails(variables.event_id) });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'planning-groups'] });
            queryClient.removeQueries({ queryKey: [...tpoGenKeys.all, 'plan-list'] });
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            sendNotification('success', 'Promo saved successfully');
            onSuccess?.(data);
        },
        onError: (error: any, variables) => {
            const isEditMode = variables.promotion_id != null;
            const detail =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                (isEditMode ? 'Failed to update promo' : 'Failed to create promo');
            sendNotification('error', detail, 5, 450);
        },
    });

    return { mutate, isPending };
};

export type { CreatePromoPayload };
export default useCreatePromoMutation;
